import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/hooks/useAuth"; // Hook de autenticación

const TwoStepVerification: React.FC = () => {
  const location = useLocation();
  const { handleVerify2FA, handleVerifyRecoveryCode, error, handleResetError } = useAuth(); // ✅ Importamos ambas funciones
  const email = location.state?.email || ""; // ✅ Obtener email de `handleLogin`
  
  const [code, setCode] = useState(""); // ✅ Input de código
  const [useRecoveryCode, setUseRecoveryCode] = useState(false); // ✅ Alternar entre 2FA y código de recuperación

  // 🔹 Manejar cambios en el input
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
  
    if (useRecoveryCode) {
      // ✅ Permitir letras, números y guiones para códigos de recuperación, hasta 13 caracteres
      if (/^[a-zA-Z0-9-]*$/.test(value) && value.length <= 13) {
        setCode(value);
      }
    } else {
      // ✅ Solo permitir números para 2FA y máximo 6 caracteres
      if (/^\d*$/.test(value) && value.length <= 6) {
        setCode(value);
      }
    }
  };

  // 🔹 Enviar código cuando el usuario presiona "Verificar"
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
  
    if (!useRecoveryCode && code.length !== 6) {
      return;
    }
  
    if (useRecoveryCode && code.length !== 13) {
      return;
    }
  
    try {
      if (useRecoveryCode) {
        // ✅ Verificar código de recuperación
        await handleVerifyRecoveryCode({ email, code });
      } else {
        // ✅ Verificar código 2FA de Google Authenticator
        await handleVerify2FA({ email, code: code });
      }
    } catch (error) {
    }
  };

  return (
    <div className="overflow-hidden px-4 dark:bg-boxdark-2 sm:px-8">
      <div className="flex h-screen flex-col items-center justify-center overflow-hidden">
        <div className="no-scrollbar overflow-y-auto py-20">
          <div className="mx-auto w-full max-w-[480px]">
            <div className="text-center">
              <h1 className="mb-2.5 text-3xl font-black leading-[48px] text-black dark:text-white">
                {useRecoveryCode ? "Usar Código de Recuperación" : "Verifica tu cuenta"}
              </h1>
              <p className="mb-7.5 font-medium">
                {useRecoveryCode
                  ? "Ingresa uno de tus códigos de recuperación."
                  : "Ingresa el código de 6 dígitos generado en Google Authenticator."}
              </p>

              <form onSubmit={handleSubmit}>
                <div className="flex justify-center mb-4">
                  <input
                    type="text"
                    value={code}
                    onChange={handleChange}
                    maxLength={useRecoveryCode ? 13 : 6} // ✅ Ajustar longitud máxima según tipo de código
                    className={`w-full ${
                      useRecoveryCode ? "max-w-[300px]" : "max-w-[200px]"
                    } rounded-md border-[1.5px] border-stroke bg-transparent px-5 py-3 text-center text-black text-xl font-mono tracking-wide outline-none transition focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary`}
                    placeholder={useRecoveryCode ? "ad0cf174-14f5" : "------"}
                  />
                </div>

                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-primary p-[13px] font-bold text-gray hover:bg-opacity-90"
                >
                  Verificar
                </button>

                {error && <p className="mt-4 text-red">{error}</p>}

                <span className="mt-5 block text-red">
                  ¡No compartas tu código de verificación con nadie!
                </span>
              </form>

              {/* 🔹 Opción para cambiar a código de recuperación */}
              <p className="mt-4 text-sm">
                {useRecoveryCode ? (
                  <>
                    ¿Tienes acceso a tu autenticador?{" "}
                    <button
                      className="text-primary font-semibold"
                      onClick={() => {
                        setUseRecoveryCode(false);
                        setCode(""); // ✅ Limpiar input al cambiar de modo
                        handleResetError(); // Limpiar el mensaje de error
                      }}
                    >
                      Usar código de 2FA
                    </button>
                  </>
                ) : (
                  <>
                    ¿No puedes acceder a tu autenticador?{" "}
                    <button
                      className="text-primary font-semibold"
                      onClick={() => {
                        setUseRecoveryCode(true);
                        setCode(""); // ✅ Limpiar input al cambiar de modo
                        handleResetError(); // Limpiar el mensaje de error
                      }}
                    >
                      Usar código de recuperación
                    </button>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoStepVerification;
